import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { EarthMap } from "../earth/EarthMap";
import {
  flyMapTo,
  geocodePlaces,
  parseWorldSearchQuery,
  type GeocodePlace,
} from "../earth/worldSearch";
import { useWorldMap } from "../contexts/WorldMapContext";
import {
  attachWorldHoverPixelLayer,
  getWorldHoverPick,
  previewWorldHoverPixel,
  confirmWorldHoverClick,
  unlockWorldHoverPick,
} from "../earth/worldHoverPixel";
import {
  attachWorldSoldPixelsLayer,
  redrawCartPixelsOnMap,
  type Map3dElement,
} from "../earth/worldSoldPixels";
import { convertDecimalToDMS, pixelIntegerFromLatLng } from "../earth/pixel";
import { socketClient } from "../api/socket";
import {
  wsLookupToPixelAndStatus,
  type WsPixelLookupContent,
} from "../api/wsPixelNormalize";
import { useCart } from "../contexts/CartContext";
import { useLocale } from "../contexts/LocaleContext";
import { hasRegionPrice, resolvePrice } from "../lib/price";
import { useToast } from "../contexts/ToastContext";
import { useEscapeKey } from "../hooks/useEscapeKey";
import {
  attachRegionPreviewLayer,
  type RegionPreviewPayload,
} from "../earth/regionPreview";
import type { Pixel } from "../types";

type WorldNavState = {
  searchQuery?: string;
  regionPreview?: RegionPreviewPayload;
};

type PopupKind = null | "buy" | "unavailable" | "sold" | "loading";

type SearchPopup =
  | null
  | { kind: "pick"; places: GeocodePlace[] }
  | { kind: "noResults"; query: string };

const LS_LAT = "lat";
const LS_LNG = "lng";

/** Legacy main.js `viennaCamera.center` + shared altitude. */
const VIENNA = { lat: 48.203, lng: 16.396, altitude: 2175.13 };
const START_RANGE = 5000;

function infoFromClickIfNeeded(
  resolved: Pixel,
  click: { lat: number; lng: number },
): Pixel {
  const lat = resolved.info?.lat;
  const lng = resolved.info?.lng;
  const valid =
    typeof lat === "number" &&
    typeof lng === "number" &&
    Number.isFinite(lat) &&
    Number.isFinite(lng) &&
    (Math.abs(lat) > 1e-7 || Math.abs(lng) > 1e-7);
  if (valid) return resolved;
  return { ...resolved, info: { lat: click.lat, lng: click.lng } };
}

// World map: click → client-side pixel integer (legacy handleMapClick math)
// → websocket `pixel` with requestId → `response:*` payload (not HTTP /pixel).
export function WorldPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { registerSearchHandler } = useWorldMap();
  const pendingSearchRef = useRef<string | null>(null);
  const mobileSelectionRef = useRef<{ pixel: number; selectedAt: number } | null>(
    null,
  );

  const [mapHost, setMapHost] = useState<HTMLElement | null>(null);
  const [searchPopup, setSearchPopup] = useState<SearchPopup>(null);
  const [startup, setStartup] = useState<{
    lat: number;
    lng: number;
    altitude: number;
  } | null>(null);
  const [popup, setPopup] = useState<PopupKind>(null);
  const [pixel, setPixel] = useState<Pixel | null>(null);
  const [pixelStatus, setPixelStatus] = useState<number>(0);

  const { items: cartItems, reserve, unreserve } = useCart();
  const toast = useToast();
  const { t, formatMoney, currency } = useLocale();

  useEffect(() => {
    const laRaw = localStorage.getItem(LS_LAT);
    const lnRaw = localStorage.getItem(LS_LNG);
    if (laRaw != null && lnRaw != null) {
      const la = Number(laRaw);
      const ln = Number(lnRaw);
      if (Number.isFinite(la) && Number.isFinite(ln)) {
        setStartup({
          lat: la,
          lng: ln,
          altitude: VIENNA.altitude,
        });
        return;
      }
    }

    if (typeof navigator === "undefined" || !navigator.geolocation) {
      setStartup(VIENNA);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const lat = pos.coords.latitude;
        const lng = pos.coords.longitude;
        localStorage.setItem(LS_LAT, String(lat));
        localStorage.setItem(LS_LNG, String(lng));
        setStartup({ lat, lng, altitude: VIENNA.altitude });
      },
      () => setStartup(VIENNA),
      {
        enableHighAccuracy: false,
        timeout: 8000,
        maximumAge: 300_000,
      },
    );
  }, []);

  useEffect(() => {
    if (!mapHost) return undefined;
    return attachWorldSoldPixelsLayer(mapHost as unknown as Map3dElement);
  }, [mapHost]);

  useEffect(() => {
    if (!mapHost) return undefined;
    return attachWorldHoverPixelLayer(mapHost as unknown as Map3dElement, {
      isPixelInCart: (id) => !!cartItems[String(id)],
    });
  }, [mapHost, cartItems]);

  useEffect(() => {
    if (!mapHost) return;
    void redrawCartPixelsOnMap(mapHost as unknown as Map3dElement, cartItems);
  }, [mapHost, cartItems]);

  const inCart = pixel ? !!cartItems[String(pixel.pixel)] : false;

  const closePopup = useCallback(() => {
    setPopup(null);
    mobileSelectionRef.current = null;
    unlockWorldHoverPick();
  }, []);

  const closeSearchPopup = useCallback(() => {
    setSearchPopup(null);
  }, []);

  const onEscapePopup = useCallback(() => {
    if (searchPopup) closeSearchPopup();
    else if (popup) closePopup();
  }, [searchPopup, popup, closeSearchPopup, closePopup]);

  useEscapeKey(searchPopup !== null || popup !== null, onEscapePopup);

  const flyToLatLng = useCallback(
    (lat: number, lng: number) => {
      if (!mapHost) return;
      flyMapTo(mapHost as unknown as google.maps.maps3d.Map3DElement, lat, lng);
    },
    [mapHost],
  );

  const handleWorldSearch = useCallback(
    async (rawQuery: string) => {
      const query = rawQuery.trim();
      if (!query) {
        toast.show(t("world.enterSearch"), "info");
        return;
      }
      if (!mapHost) {
        pendingSearchRef.current = query;
        toast.show(t("world.mapLoading"), "info");
        return;
      }

      const parsed = parseWorldSearchQuery(query);
      if (!parsed) {
        toast.show(t("world.enterSearch"), "info");
        return;
      }

      setSearchPopup(null);

      if (parsed.kind === "coords") {
        flyToLatLng(parsed.lat, parsed.lng);
        return;
      }

      if (parsed.kind === "pixel") {
        flyToLatLng(parsed.lat, parsed.lng);
        toast.show(`Pixel ${parsed.pixel}`, "success");
        return;
      }

      try {
        const places = await geocodePlaces(parsed.address);
        if (places.length === 0) {
          setSearchPopup({ kind: "noResults", query: parsed.address });
          return;
        }
        if (places.length === 1) {
          flyToLatLng(places[0].lat, places[0].lng);
          return;
        }
        setSearchPopup({ kind: "pick", places });
      } catch {
        toast.show(t("world.searchFailed"), "danger");
      }
    },
    [mapHost, flyToLatLng, toast, t],
  );

  useEffect(() => {
    registerSearchHandler(handleWorldSearch);
    return () => registerSearchHandler(null);
  }, [handleWorldSearch, registerSearchHandler]);

  const [regionPreview, setRegionPreview] = useState<RegionPreviewPayload | null>(
    null,
  );

  useEffect(() => {
    const nav = (location.state as WorldNavState | null) ?? {};
    if (nav.searchQuery) {
      pendingSearchRef.current = nav.searchQuery.trim();
    }
    if (nav.regionPreview) {
      setRegionPreview(nav.regionPreview);
    }
    if (nav.searchQuery || nav.regionPreview) {
      const keep: WorldNavState = {};
      if (nav.regionPreview && nav.searchQuery) {
        keep.searchQuery = nav.searchQuery;
      }
      navigate(location.pathname, { replace: true, state: keep });
    }
  }, [location.pathname, location.state, navigate]);

  useEffect(() => {
    if (!mapHost || !regionPreview) return undefined;
    return attachRegionPreviewLayer(
      mapHost as unknown as Map3dElement,
      regionPreview,
    );
  }, [mapHost, regionPreview]);

  useEffect(() => {
    const pending = pendingSearchRef.current;
    if (!pending || !mapHost) return;
    pendingSearchRef.current = null;
    void handleWorldSearch(pending);
  }, [mapHost, handleWorldSearch]);

  const onMapClick = useCallback(
    async ({ lat, lng }: { lat: number; lng: number }) => {
      const isMobileLayout =
        typeof window !== "undefined" &&
        window.matchMedia("(max-width: 768px)").matches;

      if (isMobileLayout) {
        const clickedInteger = pixelIntegerFromLatLng(lat, lng);
        const now = performance.now();
        const selected = mobileSelectionRef.current;

        // First tap (or tap on a different frame): only show the preview.
        if (!selected || selected.pixel !== clickedInteger) {
          const preview = previewWorldHoverPixel(clickedInteger);
          if (preview) {
            mobileSelectionRef.current = {
              pixel: preview.pixel,
              selectedAt: now,
            };
          }
          return;
        }

        // gmp-map-3d can emit both gmp-click and click for one physical tap.
        // Ignore the immediate duplicate so one tap can never skip preview.
        if (now - selected.selectedAt < 250) {
          return;
        }
      }

      confirmWorldHoverClick();
      const hover = getWorldHoverPick();
      const integer =
        hover?.pixel ?? pixelIntegerFromLatLng(lat, lng);
      const coords = hover ?? { lat, lng };

      setPopup("loading");
      setPixel({
        pixel: integer,
        info: { lat: coords.lat, lng: coords.lng },
        location: t("world.loadingInfo"),
      });
      setPixelStatus(-1);

      try {
        await socketClient.connect();
        const raw = await socketClient.awaitLegacyResponse<WsPixelLookupContent>(
          "pixel",
          { pixel: integer },
        );
        const { pixel: p, status } = wsLookupToPixelAndStatus(raw);
        const merged = infoFromClickIfNeeded(p, coords);
        setPixel(merged);
        setPixelStatus(status);

        if (status === 0) {
          const rawPrice = merged.region?.properties?.price;
          if (hasRegionPrice(rawPrice)) {
            setPopup("buy");
          } else {
            setPopup("unavailable");
            toast.show(t("world.frameUnavailable"), "info");
          }
        } else {
          setPopup("sold");
          toast.show(t("world.cellTaken"), "info");
        }
      } catch (err) {
        closePopup();
        toast.show(
          t("world.pixelLookupFailed", { error: (err as Error).message }),
          "danger",
        );
      }
    },
    [toast, closePopup, t],
  );

  const onBuy = async () => {
    if (!pixel) return;
    const r = await reserve(pixel);
    if (r.ok) {
      toast.show(t("cart.added"), "success");
      closePopup();
    } else {
      toast.show(r.error || t("cart.addFailed"), "danger");
    }
  };

  const onRemove = async () => {
    if (!pixel) return;
    const r = await unreserve(pixel.pixel);
    if (r.ok) {
      toast.show(t("cart.removed"), "success");
      closePopup();
    } else {
      toast.show(r.error || t("cart.removeFailed"), "danger");
    }
  };

  const coords =
    pixel?.info &&
    `${convertDecimalToDMS(pixel.info.lat, "lat")}, ${convertDecimalToDMS(
      pixel.info.lng,
      "lng",
    )}`;

  const priceLabel = hasRegionPrice(pixel?.region?.properties?.price)
    ? formatMoney(resolvePrice(pixel?.region?.properties?.price, currency))
    : t("common.dash");

  return (
    <section className="content world-page">
      <main
        id="map"
        style={{
          width: "100%",
          height: "calc(100vh - 320px)",
          minHeight: 500,
        }}
      >
        {!startup ? (
          <div
            style={{
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {t("world.preparing")}
          </div>
        ) : (
          <EarthMap
            key={`${startup.lat}:${startup.lng}`}
            initialCenter={startup}
            initialRange={START_RANGE}
            onMapReady={setMapHost}
            onMapDispose={() => setMapHost(null)}
            onMapClick={onMapClick}
          />
        )}
      </main>

      {/* Click in flight — same overlay pattern as legacy “loading popup” */}
      <div
        className="popup-overlay"
        id="popup-loading"
        aria-busy="true"
        aria-hidden={popup !== "loading"}
        style={popup === "loading" ? { display: "flex" } : undefined}
      >
        <div className="popup-content" role="status">
          <p style={{ margin: 0 }}>{t("world.resolving")}</p>
        </div>
      </div>

      {/* Buy popup */}
      <div
        className="popup-overlay"
        id="popup-buy"
        aria-hidden={popup !== "buy"}
        style={popup === "buy" ? { display: "flex" } : undefined}
        onClick={(e) => {
          if (e.target === e.currentTarget) closePopup();
        }}
      >
        <div
          className="popup-content"
          role="dialog"
          aria-modal="true"
          aria-labelledby="popup-title-buy"
        >
          <button
            className="close-btn"
            type="button"
            aria-label={t("profile.closePopup")}
            onClick={closePopup}
          >
            X
          </button>

          <div className="popup-head">
            <h2 id="popup-title-buy">{t("world.buyTitle")}</h2>
          </div>

          <div className="info-grid">
            <p>
              <strong>{t("common.coordinates")}:</strong>{" "}
              <span id="coordinates">{coords || t("common.dash")}</span>
            </p>
            <p>
              <strong>{t("common.address")}:</strong>{" "}
              <span id="address">{pixel?.location || t("common.dash")}</span>
            </p>
            <p>
              <strong>{t("common.price")}:</strong>{" "}
              <span id="price">{priceLabel}</span>
            </p>
          </div>

          <div className="buy-actions">
            <button className="btn-cancel secondary" onClick={closePopup}>
              {t("common.close")}
            </button>
            {!inCart ? (
              <button className="btn-gotocart" onClick={onBuy}>
                {t("world.addToCart")}
              </button>
            ) : (
              <button className="btn-removefromcart" onClick={onRemove}>
                {t("world.removeFromCart")}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Unavailable popup */}
      <div
        className="popup-overlay"
        id="popup-unavailable"
        aria-hidden={popup !== "unavailable"}
        style={popup === "unavailable" ? { display: "flex" } : undefined}
        onClick={(e) => {
          if (e.target === e.currentTarget) closePopup();
        }}
      >
        <div className="popup-content" role="dialog" aria-modal="true">
          <button className="close-btn" type="button" onClick={closePopup}>
            X
          </button>
          <div className="popup-head">
            <h2>{t("world.unavailableTitle")}</h2>
          </div>
          <div className="unavaible-note">
            <p style={{ margin: 0 }}>{t("world.unavailableNote")}</p>
            <p style={{ margin: 0 }}>{t("world.unavailableHint")}</p>
          </div>
          <div className="buy-actions">
            <button className="btn-cancel secondary" onClick={closePopup}>
              {t("common.close")}
            </button>
          </div>
        </div>
      </div>

      {/* Sold / reserved popup */}
      <div
        className="popup-overlay"
        id="popup-sold"
        aria-hidden={popup !== "sold"}
        style={popup === "sold" ? { display: "flex" } : undefined}
        onClick={(e) => {
          if (e.target === e.currentTarget) closePopup();
        }}
      >
        <div className="popup-content" role="dialog" aria-modal="true">
          <button className="close-btn" type="button" onClick={closePopup}>
            X
          </button>
          <div className="popup-head">
            <h2>
              {t("world.soldTitle")}{" "}
              <span>
                {pixelStatus === 1 || pixelStatus === 2
                  ? t("world.reserved")
                  : t("world.taken")}
              </span>
            </h2>
          </div>
          <div className="sold-note">
            <p style={{ margin: 0 }}>{t("world.soldHint")}</p>
          </div>
          <div style={{ marginTop: "1em" }}>
            <div className="image-wrap">
              <img
                src={pixel?.image || "/static/img/gal12.jpg"}
                alt={t("world.artworkAlt")}
              />
            </div>
            <div className="popup-info" style={{ textAlign: "left" }}>
              <p>
                <strong>{t("common.artist")}:</strong>{" "}
                <span>{pixel?.artistName || t("common.unknown")}</span>
              </p>
              <p>
                <strong>{t("common.location")}:</strong>{" "}
                <span>{pixel?.location || t("common.unknown")}</span>
              </p>
              <p>
                <strong>{t("common.country")}:</strong>{" "}
                <span>{pixel?.country || t("common.unknown")}</span>
              </p>
              <p>
                <strong>{t("common.coordinates")}:</strong>{" "}
                <span className="coordinates">
                  {coords || t("common.dash")}
                </span>
              </p>
              <div className="frame-meta">
                <p>
                  <strong>{t("world.imageName")}</strong>{" "}
                  <span>{pixel?.frameName || t("common.noTitle")}</span>
                </p>
              </div>
            </div>
          </div>
          <div className="buy-actions">
            <button className="btn-cancel secondary" onClick={closePopup}>
              {t("common.close")}
            </button>
          </div>
        </div>
      </div>

      <div
        className="popup-overlay"
        id="popup-search"
        aria-hidden={searchPopup === null}
        style={searchPopup !== null ? { display: "flex" } : undefined}
        onClick={(e) => {
          if (e.target === e.currentTarget) setSearchPopup(null);
        }}
      >
        <div
          className="popup-content world-search-popup"
          role="dialog"
          aria-modal="true"
        >
          <button
            className="close-btn"
            type="button"
            aria-label={t("common.close")}
            onClick={() => setSearchPopup(null)}
          >
            X
          </button>
          {searchPopup?.kind === "pick" && (
            <>
              <div className="popup-head">
                <h2>{t("world.pickPlace")}</h2>
              </div>
              <p style={{ marginTop: 0 }}>{t("world.multipleResults")}</p>
              <ul className="geocode-pick-list">
                {searchPopup.places.map((place, idx) => (
                  <li key={`${place.lat}-${place.lng}-${idx}`}>
                    <button
                      type="button"
                      className="geocode-pick-item"
                      onClick={() => {
                        flyToLatLng(place.lat, place.lng);
                        setSearchPopup(null);
                      }}
                    >
                      {place.label}
                    </button>
                  </li>
                ))}
              </ul>
            </>
          )}
          {searchPopup?.kind === "noResults" && (
            <>
              <div className="popup-head">
                <h2>{t("world.noResults")}</h2>
              </div>
              <p style={{ margin: 0 }}>
                {t("world.noResultsText", { query: searchPopup.query })}
              </p>
              <div className="buy-actions">
                <button
                  type="button"
                  className="btn-cancel secondary"
                  onClick={() => setSearchPopup(null)}
                >
                  {t("common.close")}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </section>
  );
}
